import type { ReactElement } from 'react';
import { isArray, isEmpty } from 'lodash-es';
import type { DocumentNode } from 'graphql';
import { isApolloError, type InternalRefetchQueryDescriptor } from '@apollo/client';
import { millisecondsToSeconds, secondsToMilliseconds } from 'date-fns';
import { useObservable, useObservableState } from 'observable-hooks';
import { distinctUntilChanged, map } from 'rxjs';
import type { SoundMeasuringPointCreateFromExistingMeasuringPointPayload } from '@/graphql/types';
import { getClient } from '../../../utils/apollo-client';
import { ObjectType, type ObjectTypeKey } from '../../../utils/objectType';
import { confirm, ConfirmType } from '../../confirm/Confirm';
import { addNotificationHack, NOTIFICATION_VARIANT } from '../../notifications/Notifications';
import { createMutation, createObjectNames, createQueriesForSwarmType } from './utils';
import warning from '../../../utils/logger';
import createCountdownObservable from '../../../utils/countdown';
import { createPaginatedRefetchOptions } from '../../../utils/paginatedMutation';
import type { SwarmType } from '../../../enums';

export const MS_BEFORE_RELOAD_ON_SUCCESS = secondsToMilliseconds(3);

interface CopyMutationResult {
    mutationName: string;
    mutation: DocumentNode;
}

export function createCopyMutation(
    swarmType: SwarmType,
    source: ObjectTypeKey,
    destination: ObjectTypeKey
): CopyMutationResult {
    const isProject = destination === ObjectType.PROJECT.type;

    const sourceObjectName = ObjectType[source].objectName;
    const { objectName, objectNameLowerFirst } = createObjectNames(swarmType, isProject);
    const queries = createQueriesForSwarmType(swarmType);
    const { returnFieldsFragmentName, returnFieldsFragment } = isProject
        ? queries.true
        : queries.false;

    // Creates 'AirMeasuringPointCreateFromExistingMeasuringPointInput'.
    const inputTypeName = `${objectName}CreateFromExisting${sourceObjectName}Input`;

    // Creates 'airMeasuringPointCreateFromExistingMeasuringPoint'
    const mutationName = `${objectNameLowerFirst}CreateFromExisting${sourceObjectName}`;

    // Below creates:
    // ```
    // mutation ($input: AirMeasuringPointCreateFromExistingMeasuringPointInput!) {
    //   airMeasuringPointCreateFromExistingMeasuringPoint(input: $input) {
    //     airMeasuringPoint {
    //       id,
    //       name,
    //       ...etc
    //     }
    //   }
    // }
    // ```
    return {
        mutationName,
        mutation: createMutation(
            mutationName,
            inputTypeName,
            objectNameLowerFirst,
            returnFieldsFragment,
            returnFieldsFragmentName
        ),
    };
}

export function createCopyModalQuestionText(destination: ObjectTypeKey): string {
    return interpolate(gettext('NAME_FOR_COPY_QUESTION'), [
        ObjectType[destination].translation.toLowerCase(),
    ]);
}

function SuccessNotificationMessage({ showRefreshCountdown }: { showRefreshCountdown: boolean }) {
    const refreshCountdown = useObservableState(
        useObservable(() =>
            createCountdownObservable(MS_BEFORE_RELOAD_ON_SUCCESS).pipe(
                map(millisecondsToSeconds),
                distinctUntilChanged(),
                map((seconds) => interpolate(gettext('PAGE_REFRESH_IN'), { seconds }, true))
            )
        )
    );

    if (!showRefreshCountdown) {
        return <span>{gettext('COPIED_SUCCESSFULLY')}</span>;
    }

    return (
        <span>
            {gettext('COPIED_SUCCESSFULLY')}&nbsp;
            {refreshCountdown}
        </span>
    );
}

export interface CopyModalOptions {
    id: number;
    swarmType: SwarmType;
    source: ObjectTypeKey;
    destination: ObjectTypeKey;
    /** Queries that list the copied object, refetched once the copy exists. */
    refetchQueries: ReadonlyArray<InternalRefetchQueryDescriptor>;
    // TODO(#10119): only the legacy overview page uses these, to reload itself after a copy.
    // Remove them together with that page.
    onSuccess?: () => void;
    showRefreshCountdown?: boolean;
}

interface ValidationError {
    field: string;
    message: string;
}

function intersperse<T>(arr: T[], sep: (idx: number) => ReactElement): Array<T | ReactElement> {
    return arr.flatMap((item, idx) => (idx === 0 ? [item] : [sep(idx), item]));
}

function createErrorMessagesWithBreaks(errors: ValidationError[]): Array<string | ReactElement> {
    return intersperse(
        errors.map(({ field, message }) => {
            if (message === 'This name already exists') {
                return `${gettext('NAME_ALREADY_EXISTS_ERROR')}`;
            }

            return `${field}: ${message}`;
        }),
        (i) => <br key={i} />
    );
}

function copyModal(options: CopyModalOptions): () => void {
    const {
        id,
        swarmType,
        source,
        destination,
        refetchQueries,
        onSuccess,
        showRefreshCountdown = true,
    } = options;

    const { mutation, mutationName } = createCopyMutation(swarmType, source, destination);

    return confirm({
        markdownMessage: createCopyModalQuestionText(destination),
        type: ConfirmType.PROMPT,
        // We need to mark this function as `async` to be able to use `await`
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        async onConfirm(name?: string) {
            try {
                const result = await getClient().mutate<
                    Record<string, SoundMeasuringPointCreateFromExistingMeasuringPointPayload>
                >({
                    mutation,
                    variables: {
                        input: {
                            existingId: id,
                            name,
                        },
                    },
                    ...createPaginatedRefetchOptions(refetchQueries),
                });

                const { errors } = result.data![mutationName];

                if (!isEmpty(errors)) {
                    // This should actually check on an error code but the
                    // backend does not throw that yet.
                    if (isArray(errors)) {
                        const errorMessages = createErrorMessagesWithBreaks(
                            errors as ValidationError[]
                        );

                        addNotificationHack({
                            message: (
                                <span>
                                    {errorMessages}
                                    &nbsp;
                                    <a
                                        onClick={(e) => {
                                            e.preventDefault();
                                            copyModal(options);
                                        }}
                                    >
                                        {gettext('RETRY')}
                                    </a>
                                </span>
                            ),
                            variant: NOTIFICATION_VARIANT.ERROR,
                        });
                        return;
                    }

                    throw new Error(String(errors));
                }

                addNotificationHack({
                    message: (
                        <SuccessNotificationMessage showRefreshCountdown={showRefreshCountdown} />
                    ),
                    scrollToTop: true,
                });
                onSuccess?.();
            } catch (e: unknown) {
                // Apollo errors are already logged by the `onError` function.
                if (!isApolloError(e as Error)) {
                    const error = e instanceof Error ? e : new Error(String(e));
                    warning(error);
                }

                addNotificationHack({
                    message: gettext('ERROR'),
                    variant: NOTIFICATION_VARIANT.ERROR,
                });
            }
        },
    });
}

export default copyModal;
